import { Injectable } from '@angular/core';
import { createRxDatabase, RxCollection, RxDatabase, RxDatabaseBase } from 'rxdb';
import { getRxStorageDexie } from 'rxdb/plugins/storage-dexie';


export const DB_NAME = 'senbac_wpa_db';
export const DB_VERSION = 1;
export interface UserDocumentType {
  
  
    username: string,
    password: string,
    first_name: string,
    last_name: string,
    last_login?: string,
    email: string,
    roles: string[],
  
};
export interface MissionSyncDocumentType {
    missionId: string;
    mission?: any;
    bacheliers?: any[];
    jurys?: any[];
    savedAt?: string;
}

export const BACHELIERS_SCHEMA = {
    title: 'bacheliers sync schema',
    version: 0,
    description: 'synchronised bacheliers data for a mission',
    type: 'object',
    primaryKey: 'missionId',
    properties: {
        missionId: { type: 'string', maxLength: 200 },
        mission: { type: 'object' },
        bacheliers: { type: 'array', items: { type: 'object' } },
        jurys: { type: 'array', items: { type: 'object' } },
        savedAt: { type: 'string', format: 'date-time' }
    }
};

export interface TypePieceDocumentType {
    id: string;
    items?: any[];
    savedAt?: string;
}

export interface DelivranceDocumentType {
    id: string;
    payload?: any;
    status?: 'pending' | 'sent' | 'failed';
    savedAt?: string;
}

export interface SignalementDocumentType {
    id: string;
    payload?: any;
    status?: 'pending' | 'sent' | 'failed';
    savedAt?: string;
}

export type DatabaseCollections = {
    users: RxCollection<UserDocumentType>,
    bacheliers: RxCollection<MissionSyncDocumentType>,
    missions: RxCollection<MissionSyncDocumentType>,
    typePieces: RxCollection<TypePieceDocumentType>,
    delivrances: RxCollection<DelivranceDocumentType>,
    signalements: RxCollection<SignalementDocumentType>
};
export type SenBacDb = RxDatabase<DatabaseCollections>;

export const USER_SCHEMA = {
    title: 'user schema',
    version: 0,
    description: 'describes a user',
    type: 'object',
    primaryKey: 'username',
    properties: {
        username: {
            type: 'string',
            "maxLength": 200
        },
        password: {
            type: 'string'
        },
        first_name: {
            type: 'string'
        },
        last_name: {
            type: 'string'
        },
        email: {
            type: 'string'
        },
        last_login: {
            type: 'string',
            format: 'date-time'
        },
        roles: {
            type: 'array',
            items: {
                type: 'string'
            }
        }
    }
};


@Injectable({  providedIn: 'root'
})
export class DatabaseService  {

    private db!: SenBacDb;


    constructor() { 
        this.createDatabase();
    }

    /**
     * Demande au navigateur de conserver durablement le stockage local.
     * Sans ce statut, IndexedDB est « best-effort » : sous pression disque, le navigateur
     * peut l'évincer — et donc effacer les délivrances pas encore synchronisées d'une
     * mission de plusieurs jours. Le statut est accordé sans invite sur Chrome quand l'app
     * est installée depuis l'écran d'accueil.
     * Non bloquant : un refus (ou un navigateur sans l'API) laisse l'app fonctionner.
     */
    private async requestPersistentStorage(): Promise<void> {
        try {
            if (!navigator.storage?.persist) {
                console.warn('[storage] persistance non supportée : données évinçables si le disque sature');
                return;
            }
            const persisted = await navigator.storage.persisted() || await navigator.storage.persist();
            if (persisted) {
                console.info('[storage] stockage local persistant : données à l\'abri de l\'éviction');
            } else {
                console.warn('[storage] persistance refusée : installer l\'app améliore les chances de l\'obtenir');
            }
        } catch (err) {
            console.warn('[storage] demande de persistance impossible', err);
        }
    }

    async createDatabase() {
        await this.requestPersistentStorage();
        const originalLog = console.log;
        console.log = (...args: unknown[]) => {
            if (typeof args[0] === 'string' && args[0].includes('RxDB')) return;
            originalLog.apply(console, args);
        };
        this.db = await createRxDatabase({
            name: DB_NAME,
            storage: getRxStorageDexie(),
            multiInstance: true
        });
        console.log = originalLog;
        await this.db.addCollections({
            users: {
                schema: USER_SCHEMA
            },
            bacheliers: {
                schema: BACHELIERS_SCHEMA
            },
            missions: {
                schema: BACHELIERS_SCHEMA
            },
            typePieces: {
                schema: {
                    title: 'type pieces schema',
                    version: 0,
                    description: 'cached list of type pieces',
                    type: 'object',
                    primaryKey: 'id',
                    properties: {
                        id: { type: 'string', maxLength: 200 },
                        items: { type: 'array', items: { type: 'object' } },
                        savedAt: { type: 'string', format: 'date-time' }
                    }
                }
            },
            delivrances: {
                schema: {
                    title: 'delivrances queue schema',
                    version: 0,
                    description: 'queued delivrances when offline',
                    type: 'object',
                    primaryKey: 'id',
                    properties: {
                        id: { type: 'string', maxLength: 200 },
                        payload: { type: 'object' },
                        status: { type: 'string' },
                        savedAt: { type: 'string', format: 'date-time' }
                    }
                }
            },
            signalements: {
                schema: {
                    title: 'signalements queue schema',
                    version: 0,
                    description: 'queued error reports when offline',
                    type: 'object',
                    primaryKey: 'id',
                    properties: {
                        id: { type: 'string', maxLength: 200 },
                        payload: { type: 'object' },
                        status: { type: 'string' },
                        savedAt: { type: 'string', format: 'date-time' }
                    }
                }
            }
        });
    }

    getDb() {
        if (!this.db) {
            throw new Error('Database not initialized');
        }
        return this.db;
    }

     
  
}